/** API AI Agent Hub (engine Python, `/api/v1/aihub`). Stream dùng `ai-agent-stream.ts`. */
import type { InternalAxiosRequestConfig } from 'axios';
import request from '@/services/base-service';
import { streamSse, type StreamOptions } from '@/services/knowledge/ai-agent-stream';
import type {
  AiAgentModule,
  AiCoverage,
  AiMessageFeedback,
  AiAgentDataCatalog,
  AiHubAgentRules,
  AiHubBudget,
  AiHubConversation,
  AiHubEnvelope,
  AiHubMe,
  AiHubMessage,
  AiHubPageContext,
  AiHubProvider,
  AiHubSettings,
  AiHubSettingsUpdate,
  AiHubChatLlmPrefs,
  AiHubChatLlmPrefsUpdate,
  AiConsistencyIssue,
  AiAgentOverride,
  AiCustomExpert,
  AiCustomExpertInput,
  AiHubAgent,
  AiHubRecentRun,
  AiKbEmbeddingGraph,
  AiHubInsights,
  AiHubPersona,
  AiHubSystemLlm,
  AiHubCmsAgent,
  AiHubCmsAgentBody,
  AiHubCmsAgentSettings,
  AiHubEnterpriseVerifyResult,
  AiHubTokenBudgets,
  AiAgentsCatalog,
  AiHubOverview,
  AiKbDocument,
  AiKbHit,
  AiKnowledgeBase,
  AiProposal,
  AiStepSpec,
  AiStepAgentConfig,
  AiStepRun,
  AiStepStudioRow,
  AiHubPromptListItem,
  AiHubPromptDetail,
  AiHubPromptBlocksDetail,
  AiHubPromptBlock,
  AiHubPromptPreview,
  AiHubPromptPreviewRequest,
  AiHubKbBinding,
  AiHubActionPlanMcpInfo,
  AiHubActionPlanRules,
  AiHubApRuleConfig,
  AiHubApRuleCompile,
  AiKbConnectorSpec,
  AiKnowledgeModule,
  AiKbSource,
  AiKbSourceInput,
  AiKbSourceType,
  AiKbSourceTestResult,
  AiKbSyncRun,
  AiKbOAuthAccount,
  AiFileManagerItem,
  AiDriveItem,
} from '@/types/ai-agent';

export const AI_HUB_BASE = '/v1/aihub';

/** Thông điệp lỗi dễ hiểu từ engine: `detail` (HTTPException) / `message` (envelope) thay cho «Request failed with status code …». */
export const hubErrorMessage = (e: unknown, fallback = 'Có lỗi xảy ra'): string => {
  const err = e as { response?: { data?: { detail?: unknown; message?: unknown } }; message?: string } | null;
  const detail = err?.response?.data?.detail ?? err?.response?.data?.message;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return err?.message && !/status code \d+/.test(err.message) ? err.message : fallback;
};

/** Kho chung (Data Studio, Admin site nền tảng) hay kho riêng của công ty (Admin công ty). */
export type KbScope = 'platform' | 'company';
const kbBase = (scope: KbScope) => (scope === 'company' ? '/data/kb' : '/kb');
const AUTH = { authorization: true, rejectOnError: true, silentError: true } as InternalAxiosRequestConfig;

const get = <T>(path: string, params?: Record<string, unknown>) =>
  request.get(`${AI_HUB_BASE}${path}`, { ...AUTH, params }) as unknown as Promise<AiHubEnvelope<T>>;
const post = <T>(path: string, body?: unknown) =>
  request.post(`${AI_HUB_BASE}${path}`, body, AUTH) as unknown as Promise<AiHubEnvelope<T>>;
const put = <T>(path: string, body?: unknown) =>
  request.put(`${AI_HUB_BASE}${path}`, body, AUTH) as unknown as Promise<AiHubEnvelope<T>>;
const del = <T>(path: string) =>
  request.delete(`${AI_HUB_BASE}${path}`, AUTH) as unknown as Promise<AiHubEnvelope<T>>;

export interface ChatStreamBody {
  message: string;
  conversation_id?: string | null;
  scope_type?: 'global' | 'step' | 'template_test';
  scope_key?: string;
  page_context?: AiHubPageContext;
  kb_ids?: string[];
  /** Dữ liệu form B1 (ngành, điểm mạnh, CSF, đối thủ…) khi chat từ /danh-gia */
  user_context?: Record<string, unknown>;
  /** Bật reasoning/thinking (Ollama Qwen3, …) */
  thinking?: boolean;
  /** Temperature 0–2 (UI label: Threshold) */
  threshold?: number;
  /** Override pill — không lưu tenant */
  provider?: string;
  model?: string;
  api_key?: string;
  base_url?: string | null;
}

/** Kết nối AI ngoài (MCP bằng khoá người dùng tự tạo) — engine `modules/connect`. */
export type AiConnectScope = 'read' | 'write';
export interface AiConnectToken {
  id: string;
  user_id: string;
  user_name: string;
  name: string;
  client: string;
  scope: AiConnectScope;
  token_prefix: string;
  expires_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  status: 'active' | 'expired' | 'revoked';
  /** Chỉ có ngay sau khi tạo (hiện một lần). */
  token?: string;
}
export interface AiConnectTool {
  name: string;
  kind: 'read' | 'write' | 'delete';
  description: string;
  inputSchema: { properties?: Record<string, { type?: string; description?: string }>; required?: string[] };
  needsConfirm: boolean;
}
export interface AiConnectInfo {
  mcpUrl: string;
  serverEnabled: boolean;
  companyEnabled: boolean;
  isAdmin: boolean;
  scopes: AiConnectScope[];
  days: number[];
  tools: AiConnectTool[];
  approval: string;
}
/** Trang Giám sát AI (engine `/monitor/*`). */
/** Trung tâm quy tắc AI (engine modules/rules): 5 loại, mỗi loại một điểm thực thi chung. */
export type AiRuleType = 'guidance' | 'tool' | 'content' | 'data' | 'budget';
export type AiRuleScope = 'company' | 'module' | 'agent' | 'skill';
export interface AiRuleInput {
  type: AiRuleType;
  scope?: AiRuleScope;
  target?: string;
  roles?: string[];
  pages?: string[];
  title?: string;
  text?: string;
  condition?: { tools?: string[]; keywords?: string[]; refs?: string[]; features?: string[] };
  action?: { mode?: string; reply?: string; max_tokens?: number; max_llm_calls?: number };
  enabled?: boolean;
}
export interface AiRule extends Required<Omit<AiRuleInput, 'condition' | 'action'>> {
  id: string;
  condition: NonNullable<AiRuleInput['condition']>;
  action: NonNullable<AiRuleInput['action']>;
  version: number;
  updated_by: string | null;
  updated_at: string;
  explain: string;
}
export interface AiRuleTemplate extends AiRuleInput {
  group: string;
  explain: string;
}
export interface AiRulesCatalog {
  types: Record<AiRuleType, string>;
  roles: Record<string, string>;
  tool_modes: Record<string, string>;
  content_modes: Record<string, string>;
  features: string[];
  modules: string[];
  agents: string[];
  skills: string[];
  tools: Record<string, { name: string; label: string; read_only: boolean; destructive: boolean; needs_approval: boolean }[]>;
  refs: { value: string; label: string }[];
}
export interface AiRulesPayload {
  items: AiRule[];
  system_rules: { code: string; text: string; enabled: boolean }[];
  templates: AiRuleTemplate[];
  catalog: AiRulesCatalog;
  version: string;
}
export interface AiRuleVersion {
  version: number;
  snapshot: AiRuleInput & { title: string };
  change: string;
  updated_by: string | null;
  created_at: string;
  explain: string;
}
export interface AiRuleHistoryRow {
  rule_id: string;
  version: number;
  title: string;
  type: AiRuleType;
  change: string;
  updated_by: string | null;
  created_at: string;
}
export interface AiRuleSimulation {
  context: { role: string; module: string; agent: string; route: string };
  blocked_reply: string | null;
  tools: { allowed: string[]; blocked: string[]; approval: string[] };
  data: { denied: string[] };
  budget: { max_tokens?: number; max_llm_calls?: number };
  guidance: string;
  masked_sample: string;
  fired: { id: string; version: number; type: AiRuleType; title: string }[];
}
export interface AiRuleProposal {
  rule: (AiRuleInput & { title: string }) | null;
  explain?: string;
  error?: string;
  source: string;
}
/** Kỹ năng của master agent (engine modules/skills). source: toppion = mặc định, company = của công ty;
 *  expert / persona = chuyên gia công ty, tính cách AI (chỉ xem). */
export interface AiSkill {
  id: string;
  key: string;
  module: string;
  title: string;
  description: string;
  body: string;
  tools: string[];
  source: 'toppion' | 'company' | 'expert' | 'persona';
  enabled: boolean;
  published_version?: number | null;
  versions?: number;
  overridden?: boolean;
  readonly?: boolean;
  updated_by?: string | null;
  updated_at?: string | null;
}
export interface AiSkillVersion {
  version: number;
  body: string;
  tools: string[];
  note: string;
  created_by: string | null;
  created_at: string;
}
export interface AiSkillInput {
  key: string;
  module: string;
  title: string;
  description: string;
  body: string;
  tools: string[];
  note?: string;
}
export interface AiPromptVersion {
  version: number;
  content: string;
  status: 'draft' | 'published' | 'archived';
  note: string;
  created_by: string | null;
  created_at: string;
}
/** Công cụ của agent (registry engine): nhãn, đọc / ghi, cần duyệt. */
export interface AiAgentTool {
  name: string;
  enabled: boolean;
  label?: string | null;
  read_only?: boolean | null;
  destructive?: boolean;
  needs_approval?: boolean;
}
export interface AiMonitorSummary {
  feedback?: { up: number; down: number; like_rate: number | null };
  days: number;
  runs: number;
  failed: number;
  error_rate: number;
  running: number;
  tokens: number;
  p50_ms: number | null;
  p95_ms: number | null;
  pending_approvals: number;
}
export interface AiMonitorPoint { day: string; runs: number; failed: number; tokens: number }
export interface AiMonitorBreakdownRow { key: string; runs: number; failed: number; error_rate: number; tokens: number; p95_ms: number | null }
export type AiMonitorBreakdownBy = 'feature' | 'module' | 'agent' | 'model' | 'user' | 'error';
export interface AiMonitorRun {
  run_id: string;
  tenant_id: string;
  user_id: string | null;
  feature: string;
  module: string | null;
  agent: string | null;
  model: string | null;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  error_code: string | null;
  error?: string | null;
  duration_ms: number | null;
  tokens: number | null;
  llm_calls: number | null;
  rules_fired?: { id: string; version: number; type: string; title: string }[] | null;
  rules_version?: string | null;
  started_at: string;
  finished_at: string | null;
}
type MonitorQuery = { days: number; tenant?: string };

/** Lệnh ghi dựng qua MCP đang chờ duyệt (lệnh xoá chỉ duyệt được trên Toppion). */
export interface AiConnectPending {
  run_id: string;
  interrupt_id: string;
  tool: string | null;
  created_at: string | null;
  plans: Record<string, unknown>[];
  is_delete: boolean;
}

export interface AiConnectLog {
  id: number;
  token_id: string | null;
  user_id: string;
  user_name: string | null;
  token_name: string | null;
  tool: string;
  ok: boolean;
  error: string | null;
  latency_ms: number | null;
  created_at: string;
}

export interface AiWritePermissionRow {
  group: string;
  label: string;
  ops: { create: boolean; update: boolean; delete: boolean };
  /** Bubble: page = đưa gợi ý ra màn chính (không ghi DB), write = thẻ duyệt, engine ghi. */
  mode: 'page' | 'write';
}

export const aiAgentApi = {
  writePermissions: () => get<AiWritePermissionRow[]>('/agents/write-permissions'),
  saveWritePermissions: (rows: AiWritePermissionRow[]) => put<AiWritePermissionRow[]>('/agents/write-permissions', { rows }),
  agentTools: (agentId: string) => get<AiAgentTool[]>(`/agents/${encodeURIComponent(agentId)}/tools`),
  saveAgentTools: (agentId: string, tools: string[]) => put<null>(`/agents/${encodeURIComponent(agentId)}/tools`, { tools }),
  connectInfo: () => get<AiConnectInfo>('/connect/info'),
  connectTokens: (all = false) => get<AiConnectToken[]>('/connect/tokens', all ? { all: true } : undefined),
  connectCreateToken: (body: { name: string; scope: AiConnectScope; days: number; client?: string; request_header?: unknown }) =>
    post<AiConnectToken>('/connect/tokens', body),
  connectRevokeToken: (id: string) => del<null>(`/connect/tokens/${id}`),
  connectLogs: (days = 7, mine = false) => get<AiConnectLog[]>('/connect/logs', mine ? { days, mine: true } : { days }),
  connectPending: () => get<AiConnectPending[]>('/connect/pending'),
  monitorSummary: (q: MonitorQuery) => get<AiMonitorSummary>('/monitor/summary', q),
  monitorTimeseries: (q: MonitorQuery) => get<AiMonitorPoint[]>('/monitor/timeseries', q),
  monitorBreakdown: (by: AiMonitorBreakdownBy, q: MonitorQuery) => get<AiMonitorBreakdownRow[]>('/monitor/breakdown', { ...q, by }),
  monitorRuns: (q: MonitorQuery & { status?: string; module?: string; feature?: string; page?: number; size?: number }) =>
    get<{ total: number; page: number; size: number; items: AiMonitorRun[] }>('/monitor/runs', q),
  connectSettings: (mcpEnabled: boolean) => put<{ companyEnabled: boolean }>('/connect/settings', { mcp_enabled: mcpEnabled }),
  me: () => get<AiHubMe>('/me'),
  actionPlanMcpInfo: () => get<AiHubActionPlanMcpInfo>('/action-plan/mcp-info'),
  /** Rule trợ lý Action Plan (mã R01…) + cài đặt đơn vị. */
  actionPlanRules: () => get<AiHubActionPlanRules>('/action-plan/rules'),
  /** Engine ghi plan Action Plan người dùng vừa bấm Xác nhận (thẻ tạo ở FE). */
  executeActionPlan: (plan: Record<string, unknown>) =>
    post<{ ok: boolean; id?: string; action?: string }>('/action-plan/execute', { plan }),
  /** «Quy tắc đang áp» 5 tầng của một agent (hệ thống → nền tảng → công ty → module → agent). */
  agentRules: (agentId: string) => get<AiHubAgentRules>(`/agents/${encodeURIComponent(agentId)}/rules`),
  saveActionPlanRules: (body: { config: AiHubApRuleConfig; customRules: string }) =>
    put<AiHubActionPlanRules>('/action-plan/rules', body),
  compileActionPlanRules: (body: { config: AiHubApRuleConfig; customRules: string }) =>
    post<AiHubApRuleCompile>('/action-plan/rules/compile', body),
  getSettings: () =>
    get<{
      settings: AiHubSettings;
      providers: AiHubProvider[];
      budget: AiHubBudget;
      personas: AiHubPersona[];
      system_llm: { provider: string; model: string };
      can_manage_system: boolean;
    }>('/settings'),
  systemLlm: () => get<{ settings: AiHubSystemLlm; providers: AiHubProvider[] }>('/settings/system'),
  saveSystemLlm: (body: { provider: string; model?: string; base_url?: string | null; api_key?: string; clear_api_key?: boolean }) =>
    put<AiHubSystemLlm>('/settings/system', body),
  /** Agent riêng của CMS mobile (duyệt doanh nghiệp, chat mobile): mô hình riêng, provider rỗng = dùng mô hình hệ thống. Bật / tắt duyệt doanh nghiệp ở FE.CMS. */
  cmsAgents: () => get<{ agents: AiHubCmsAgent[]; mobile_module: string }>('/settings/agents'),
  saveCmsAgent: (id: string, body: AiHubCmsAgentBody) => put<AiHubCmsAgentSettings>(`/settings/agents/${id}`, body),
  testCmsAgent: (id: string, body: AiHubCmsAgentBody) => post<{ reply: string } | null>(`/settings/agents/${id}/test`, body),
  /** Chạy thử Duyệt doanh nghiệp đúng luồng FE.CMS: giấy tờ + MST (+ mô hình đang chọn trên form). */
  tryCmsEnterprise: (body: { files: File[]; mst: string; ten_dn?: string } & Partial<AiHubCmsAgentBody>) => {
    const form = new FormData();
    body.files.forEach((f) => form.append('files', f));
    form.append('mst', body.mst);
    (['ten_dn', 'provider', 'model', 'base_url', 'api_key'] as const).forEach((k) => {
      const v = body[k];
      if (v) form.append(k, String(v));
    });
    return request.post(`${AI_HUB_BASE}/settings/agents/cms_mobile_enterprise/try`, form, AUTH) as unknown as Promise<
      AiHubEnvelope<AiHubEnterpriseVerifyResult>
    >;
  },
  /** Chạy thử Chat mobile đúng đường app mobile; `instructions` = lời dặn nháp (chưa lưu). */
  tryCmsChat: (body: { question: string; instructions?: string }) =>
    post<{ answer: string; model: string }>('/settings/agents/cms_mobile_chat/try', body),
  /** Hạn mức token theo công ty (Admin site nền tảng). */
  tokenBudgets: () => get<AiHubTokenBudgets>('/settings/budgets'),
  /** tenantId = 'default' → hạn mức mặc định; value null = theo mặc định, 0 = không giới hạn. */
  saveTokenBudget: (tenantId: string, value: number | null) =>
    put<null>(`/settings/budgets/${encodeURIComponent(tenantId)}`, { daily_token_budget: value }),
  insights: (days = 7) => get<AiHubInsights>('/insights', { days }),
  /** Bản đồ phủ AI theo module: kỹ năng, công cụ, tri thức, quy tắc, lượt dùng 7 ngày, việc còn thiếu. */
  coverage: () => get<AiCoverage>('/coverage'),
  saveSettings: (body: AiHubSettingsUpdate) => put<AiHubSettings>('/settings', body),
  testSettings: () => post<{ reply: string } | null>('/settings/test'),
  /** Thử mô hình đang chọn trên form «Mô hình hệ thống» (chưa lưu cũng được). */
  testSystemLlm: (body: { provider: string; model?: string | null; base_url?: string | null; api_key?: string }) =>
    post<{ reply: string } | null>('/settings/system/test', body),
  getChatLlmPrefs: () => get<AiHubChatLlmPrefs>('/chat/llm-prefs'),
  saveChatLlmPrefs: (body: AiHubChatLlmPrefsUpdate) => put<AiHubChatLlmPrefs>('/chat/llm-prefs', body),
  conversations: (scope_type?: string) => get<AiHubConversation[]>('/chat/conversations', { scope_type }),
  messages: (conversationId: string) => get<AiHubMessage[]>(`/chat/conversations/${conversationId}`),
  /** 👍 (1) / 👎 (-1) / bỏ phiếu (0) cho câu trả lời AI của chính mình. */
  messageFeedback: (messageId: string, rating: 1 | -1 | 0, comment = '', route?: string) =>
    post<{ rating: number | null }>(`/chat/messages/${messageId}/feedback`, { rating, comment, route }),
  feedbackList: (q: { days?: number; rating?: 1 | -1; tenant?: string }) => get<AiMessageFeedback[]>('/chat/feedback', q),
  /** JSONL các câu 👎 cho bộ eval (đánh dấu đã xuất). */
  feedbackExport: (days = 30) => request.get(`${AI_HUB_BASE}/chat/feedback/export`, {
    params: { days }, responseType: 'blob', authorization: true,
  } as InternalAxiosRequestConfig),
  deleteConversation: (conversationId: string) => del<null>(`/chat/conversations/${conversationId}`),
  chatStream: (body: ChatStreamBody, options: StreamOptions) => streamSse(`${AI_HUB_BASE}/chat/stream`, body, options),

  stepSpecs: () => get<AiStepSpec[]>('/steps/specs'),
  proposeStream: (stepKey: string, body: ProposeBody, options: StreamOptions) => {
    const [step, sub] = stepKey.split('.');
    return streamSse(`${AI_HUB_BASE}/steps/${step}/${sub}/propose`, body, options);
  },
  latestProposal: (stepKey: string) => {
    const [step, sub] = stepKey.split('.');
    return get<{ id: string; status: string; payload: AiProposal } | null>(`/steps/${step}/${sub}/latest`);
  },
  proposalAction: (proposalId: string, action: 'applied' | 'rejected' | 'undone', body: ProposalActionBody = {}) =>
    post<null>(`/steps/proposals/${proposalId}/${action}`, body),
  aiItems: (step: string) => get<string[]>(`/steps/${step}/ai-items`),
  stepStudio: () => get<AiStepStudioRow[]>('/steps/studio'),
  saveStepAgent: (stepKey: string, body: AiStepAgentConfig) => {
    const [step, sub] = stepKey.split('.');
    return put<null>(`/steps/studio/${step}/${sub}`, body);
  },
  resetStepAgent: (stepKey: string) => {
    const [step, sub] = stepKey.split('.');
    return post<null>(`/steps/studio/${step}/${sub}/reset`);
  },
  /** Playground: chạy đủ chuỗi agent với cấu hình hiện tại, không lưu proposal. */
  testStepAgentStream: (
    stepKey: string,
    body: Partial<ProposeBody> & { from_proposal_id?: string },
    options: StreamOptions,
  ) => {
    const [step, sub] = stepKey.split('.');
    return streamSse(`${AI_HUB_BASE}/steps/studio/${step}/${sub}/test`, body, options);
  },
  stepAgentRuns: (stepKey: string) => {
    const [step, sub] = stepKey.split('.');
    return get<AiStepRun[]>(`/steps/studio/${step}/${sub}/runs`);
  },
  /** Admin công ty: hạn mức token / người / ngày (null / 0 = không giới hạn riêng). */
  setUserBudget: (value: number | null) => put<AiHubBudget>('/settings/user-budget', { user_daily_token_budget: value }),
  rules: () => get<AiRulesPayload>('/rules'),
  ruleCreate: (rule: AiRuleInput) => post<AiRule>('/rules', { rule }),
  ruleUpdate: (id: string, rule: AiRuleInput) => put<AiRule>(`/rules/${id}`, { rule }),
  ruleToggle: (id: string, enabled: boolean) => put<AiRule>(`/rules/${id}/enabled`, { enabled }),
  ruleDelete: (id: string) => del<null>(`/rules/${id}`),
  ruleVersions: (id: string) => get<AiRuleVersion[]>(`/rules/${id}/versions`),
  ruleRestore: (id: string, version: number) => post<AiRule>(`/rules/${id}/restore`, { version }),
  rulesHistory: () => get<AiRuleHistoryRow[]>('/rules/history'),
  rulePreview: (rule: AiRuleInput) => post<{ rule: AiRuleInput; explain: string }>('/rules/preview', { rule }),
  rulesCompile: (text: string) => post<AiRuleProposal[]>('/rules/compile', { text }),
  rulesSimulate: (body: { question: string; role: 'admin' | 'staff'; page_context?: Record<string, unknown>; agent?: string; draft?: AiRuleInput[] }) =>
    post<AiRuleSimulation>('/rules/simulate', body),
  skills: () => get<{ items: AiSkill[]; platform: boolean; modules: string[] }>('/skills'),
  skillCreate: (body: AiSkillInput & { toppion?: boolean }) => post<{ id: string; version: number }>('/skills', body),
  skillUpdate: (id: string, body: Partial<Pick<AiSkill, 'module' | 'title' | 'description' | 'enabled'>>) => put<null>(`/skills/${id}`, body),
  skillDelete: (id: string) => del<null>(`/skills/${id}`),
  skillVersions: (id: string) => get<{ published_version: number | null; items: AiSkillVersion[] }>(`/skills/${id}/versions`),
  skillAddVersion: (id: string, body: { body: string; tools: string[]; note?: string; publish?: boolean }) =>
    post<{ version: number }>(`/skills/${id}/versions`, body),
  skillPublish: (id: string, version: number) => post<null>(`/skills/${id}/publish`, { version }),
  skillToggleDefault: (key: string, enabled: boolean) => put<null>(`/skills/toppion/${encodeURIComponent(key)}/enabled`, { enabled }),
  /** Chạy thử master agent với bản nháp kỹ năng (SSE; kết quả ở event `message`). */
  skillTestStream: (body: Partial<AiSkillInput> & { question: string; page_context?: Record<string, unknown> }, options: StreamOptions) =>
    streamSse(`${AI_HUB_BASE}/skills/test`, body, options),
  promptVersions: (key: string) => get<AiPromptVersion[]>(`/prompts/${encodeURIComponent(key)}/versions`),
  promptAddVersion: (key: string, body: { content: string; note?: string; publish?: boolean }) =>
    post<{ version: number }>(`/prompts/${encodeURIComponent(key)}/versions`, body),
  promptPublishVersion: (key: string, version: number) => post<null>(`/prompts/${encodeURIComponent(key)}/versions/${version}/publish`, {}),
  promptUnpublish: (key: string) => post<null>(`/prompts/${encodeURIComponent(key)}/versions/unpublish`),
  runDetail: (runId: string) =>
    get<AiStepRun & { events: { seq: number; type: string; data: Record<string, unknown>; ts: string }[] }>(`/runs/${runId}`),
  consistency: (plan: Record<string, unknown>) =>
    post<{ issues: AiConsistencyIssue[]; summary: string; model_ok: boolean }>('/steps/consistency', { plan }),

  overview: () => get<AiHubOverview>('/overview'),

  kbList: (module?: string) => get<AiKnowledgeBase[]>('/kb', module ? { module } : undefined),
  kbCreate: (name: string, description = '', module = 'chung') => post<AiKnowledgeBase>('/kb', { name, description, module }),
  kbUpdate: (id: string, name: string, description = '', exposeMcp?: boolean, module?: string) =>
    put<AiKnowledgeBase>(`/kb/${id}`, {
      name,
      description,
      ...(exposeMcp === undefined ? {} : { expose_mcp: exposeMcp }),
      ...(module === undefined ? {} : { module }),
    }),
  kbModules: () => get<AiKnowledgeModule[]>('/kb/modules'),
  kbModuleCreate: (body: Omit<AiKnowledgeModule, 'is_system' | 'kb_count'>) => post<AiKnowledgeModule>('/kb/modules', body),
  kbModuleUpdate: (key: string, body: Partial<Omit<AiKnowledgeModule, 'key' | 'is_system' | 'kb_count'>>) =>
    put<AiKnowledgeModule>(`/kb/modules/${key}`, body),
  kbModuleDelete: (key: string) => del<null>(`/kb/modules/${key}`),
  kbDelete: (id: string) => del<null>(`/kb/${id}`),
  /** sourceId: id nguồn hoặc 'manual' (chỉ tệp tải lên tay). */
  kbDocuments: (id: string, params?: { source_id?: number | 'manual'; status?: string; q?: string }) =>
    get<AiKbDocument[]>(`/kb/${id}/documents`, params),
  kbUpload: (id: string, files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    return request.post(`${AI_HUB_BASE}/kb/${id}/documents`, form, AUTH) as unknown as Promise<
      AiHubEnvelope<{ filename: string; ok: boolean; duplicate?: boolean; error?: string }[]>
    >;
  },
  kbDeleteDocument: (id: string, docId: number) => del<null>(`/kb/${id}/documents/${docId}`),
  /** Nguồn dữ liệu của agent (tab «Dữ liệu»): mặc định, công ty bật/tắt, hiệu lực, nguồn có thể chọn. */
  agentData: (agentId: string) => get<AiAgentDataCatalog>(`/agents/${encodeURIComponent(agentId)}/data`),
  saveAgentData: (agentId: string, bindings: { source_ref: string; enabled: boolean }[]) =>
    put<AiAgentDataCatalog>(`/agents/${encodeURIComponent(agentId)}/data`, { bindings }),
  /** Kho riêng của công ty (Admin công ty, mọi site). */
  tenantDataSources: () => get<{ kbs: (AiKnowledgeBase & { sources?: AiKbSource[] })[]; modules: AiKnowledgeModule[] }>('/data/sources'),
  tenantKbCreate: (body: { name: string; description?: string; module: string }) => post<AiKnowledgeBase>('/data/kb', body),
  tenantKbDelete: (id: string) => del<null>(`/data/kb/${id}`),
  tenantKbDocuments: (id: string) => get<AiKbDocument[]>(`/data/kb/${id}/documents`),
  tenantKbUpload: (id: string, files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    return request.post(`${AI_HUB_BASE}/data/kb/${id}/documents`, form, AUTH) as unknown as Promise<
      AiHubEnvelope<{ filename: string; ok: boolean; duplicate?: boolean; error?: string }[]>
    >;
  },
  kbRetryDocument: (id: string, docId: number) => post<null>(`/kb/${id}/documents/${docId}/retry`),
  kbSearch: (id: string, query: string, k = 6) => post<AiKbHit[]>(`/kb/${id}/search`, { query, k }),
  kbEmbeddingGraph: (id: string, params: { limit?: number; k?: number; min_sim?: number } = {}) =>
    get<AiKbEmbeddingGraph>(`/kb/${id}/embedding-graph`, params),

  // Data Studio — nguồn dữ liệu. scope 'platform' = kho chung (Admin site nền tảng, `/kb/…`);
  // 'company' = kho riêng của công ty (Admin công ty, `/data/kb/…`, loại nguồn giới hạn)
  kbConnectors: (scope: KbScope = 'platform') =>
    get<{ connectors: AiKbConnectorSpec[]; google_configured: boolean; filemanager_schedule: boolean }>(
      scope === 'company' ? '/data/connectors' : '/kb/connectors'),
  kbSources: (kbId: string, scope: KbScope = 'platform') => get<AiKbSource[]>(`${kbBase(scope)}/${kbId}/sources`),
  kbSourceCreate: (kbId: string, body: AiKbSourceInput, scope: KbScope = 'platform') =>
    post<{ source: AiKbSource; run: AiKbSyncRun | null }>(`${kbBase(scope)}/${kbId}/sources`, body),
  kbSourceUpdate: (
    kbId: string,
    sourceId: number,
    body: Partial<Omit<AiKbSourceInput, 'type' | 'sync_now'>> & { enabled?: boolean; take_ownership?: boolean },
    scope: KbScope = 'platform',
  ) => put<AiKbSource>(`${kbBase(scope)}/${kbId}/sources/${sourceId}`, body),
  kbSourceDelete: (kbId: string, sourceId: number, keepDocuments: boolean, scope: KbScope = 'platform') =>
    del<null>(`${kbBase(scope)}/${kbId}/sources/${sourceId}?keep_documents=${keepDocuments}`),
  kbSourceTest: (
    kbId: string,
    body: { type: AiKbSourceType; config: Record<string, unknown>; secrets?: Record<string, unknown>; source_id?: number; oauth_account_id?: number | null },
    scope: KbScope = 'platform',
  ) => post<AiKbSourceTestResult>(`${kbBase(scope)}/${kbId}/sources/test`, body),
  kbSourceSync: (kbId: string, sourceId: number, scope: KbScope = 'platform') =>
    post<AiKbSyncRun>(`${kbBase(scope)}/${kbId}/sources/${sourceId}/sync`),
  kbSyncRuns: (kbId: string, sourceId?: number, scope: KbScope = 'platform') =>
    get<AiKbSyncRun[]>(`${kbBase(scope)}/${kbId}/sync-runs`, sourceId ? { source_id: sourceId } : undefined),
  kbSyncRun: (kbId: string, runId: number) => get<AiKbSyncRun>(`/kb/${kbId}/sync-runs/${runId}`),
  kbSyncRunCancel: (kbId: string, runId: number) => post<null>(`/kb/${kbId}/sync-runs/${runId}/cancel`),
  kbFileManagerBrowse: (path: string, scope: KbScope = 'platform') =>
    post<AiFileManagerItem[]>(scope === 'company' ? '/data/filemanager/browse' : '/kb/filemanager/browse', { path }),
  kbGoogleAccounts: () => get<AiKbOAuthAccount[]>('/kb/oauth/google/accounts'),
  kbGoogleStart: () => post<{ auth_url: string }>('/kb/oauth/google/start'),
  kbGoogleDisconnect: (accountId: number) => del<null>(`/kb/oauth/google/accounts/${accountId}`),
  kbGoogleBrowse: (accountId: number, params?: { parent?: string; q?: string }) =>
    get<AiDriveItem[]>(`/kb/oauth/google/accounts/${accountId}/browse`, params),

  agents: () => get<AiAgentsCatalog>('/agents'),
  agentsRegistry: () => get<AiHubAgent[]>('/agents/registry'),
  /** Lượt chạy gần đây: theo loại (chat…) hoặc theo agent (`agent` — mọi loại lượt chạy của agent đó). */
  recentRuns: (params: { kind?: 'chat' | 'step' | 'mcp' | 'cms'; agent?: string; limit?: number } = {}) =>
    get<AiHubRecentRun[]>('/runs', { limit: 30, ...params }),
  saveAgentOverride: (agentId: string, body: AiAgentOverride) => put<AiAgentOverride>(`/agents/${agentId}/override`, body),
  customExperts: () => get<AiCustomExpert[]>('/agents/custom'),
  customExpertDraft: (need: string, module?: string | null) =>
    post<Pick<AiCustomExpertInput, 'label' | 'description' | 'prompt'> & { rubric?: string }>('/agents/custom/draft', { need, ...(module ? { module } : {}) }),
  customExpertModules: () => get<AiAgentModule[]>('/agents/custom/modules'),
  /** Chạy thử trực tiếp một agent (không qua Master) — chuyên gia có sẵn / agent của công ty. */
  tryAgent: (agentId: string, question: string) =>
    post<{ answer: string; model: string }>(`/agents/${encodeURIComponent(agentId)}/try`, { question }),
  customExpertVersions: (id: string) => get<AiPromptVersion[]>(`/agents/custom/${id}/versions`),
  customExpertVersionPublish: (id: string, version: number) => post<null>(`/agents/custom/${id}/versions/${version}/publish`, {}),
  customExpertCreate: (body: AiCustomExpertInput) => post<AiCustomExpert>('/agents/custom', body),
  customExpertUpdate: (id: string, body: AiCustomExpertInput) => put<AiCustomExpert>(`/agents/custom/${id}`, body),
  customExpertDelete: (id: string) => del<null>(`/agents/custom/${id}`),

  prompts: () => get<AiHubPromptListItem[]>('/prompts'),
  prompt: (key: string) => get<AiHubPromptDetail>(`/prompts/${key}`),
  savePrompt: (key: string, content: string) => put<AiHubPromptDetail>(`/prompts/${key}`, { content }),
  resetPrompt: (key: string) => post<AiHubPromptDetail>(`/prompts/${key}/reset`),
  promptBlocks: (key: string) => get<AiHubPromptBlocksDetail>(`/prompts/${key}/blocks`),
  savePromptBlock: (
    key: string,
    blockKey: string,
    body: Partial<Pick<AiHubPromptBlock, 'title' | 'content' | 'enabled'>> & { kb_bindings?: AiHubKbBinding[] },
  ) => put<AiHubPromptBlock>(`/prompts/${key}/blocks/${blockKey}`, body),
  createPromptBlock: (key: string, body: { title: string; content?: string; enabled?: boolean }) =>
    post<AiHubPromptBlock>(`/prompts/${key}/blocks`, body),
  deletePromptBlock: (key: string, blockKey: string) => del<null>(`/prompts/${key}/blocks/${blockKey}`),
  reorderPromptBlocks: (key: string, ordered_keys: string[]) =>
    put<AiHubPromptBlocksDetail>(`/prompts/${key}/blocks/reorder`, { ordered_keys }),
  resetPromptBlocks: (key: string) => post<AiHubPromptBlocksDetail>(`/prompts/${key}/blocks/reset`),
  promptPreview: (key: string, body: AiHubPromptPreviewRequest) =>
    post<AiHubPromptPreview>(`/prompts/${key}/preview`, body),
};

export interface ProposeBody {
  snapshot: Record<string, unknown>;
  context?: Record<string, unknown>;
  instruction?: string;
}

export interface ProposalActionBody {
  item_refs?: string[];
  saved_ids?: string[];
  total_items?: number;
  save_endpoint?: string;
  result_ok?: boolean;
  error?: string | null;
}
